import { useState, type ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import { FiCopy, FiCheck, FiMail, FiPackage } from "react-icons/fi";
import { formatRelativeTime, formatPrice } from "../lib/format";

type Tab = "emails" | "items";

export const Route = createFileRoute("/")({
	component: InboxPage,
	validateSearch: (search: Record<string, unknown>): { tab: Tab } => ({
		tab: search.tab === "items" ? "items" : "emails",
	}),
});

function generateAddressId(): string {
	const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
	let result = "";
	for (let i = 0; i < 6; i++) {
		result += chars[Math.floor(Math.random() * chars.length)];
	}
	return result;
}

function useIngestAddress() {
	const { data: session } = useSession();
	const queryClient = useQueryClient();

	const addressQuery = useQuery({
		queryKey: ["ingest-address", session?.user?.id],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("ingest_addresses")
				.select("*")
				.eq("user_id", session!.user.id)
				.maybeSingle();
			if (error) throw error;
			return data;
		},
		enabled: !!session?.user,
	});

	const provisionMutation = useMutation({
		mutationFn: async () => {
			const { data, error } = await supabase
				.from("ingest_addresses")
				.insert({
					user_id: session!.user.id,
					address_id: generateAddressId(),
				})
				.select()
				.single();
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["ingest-address"] });
		},
	});

	// Auto-provision if user has no address
	if (
		addressQuery.data === null &&
		!provisionMutation.isPending &&
		!provisionMutation.isSuccess
	) {
		provisionMutation.mutate();
	}

	return {
		address: addressQuery.data ?? provisionMutation.data,
		isLoading: addressQuery.isLoading || provisionMutation.isPending,
	};
}

function useIngestEmails(addressId: string | undefined, active: boolean) {
	return useQuery({
		queryKey: ["ingest-emails", addressId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("ingest_emails")
				.select("id, from_name, from_address, subject, received_at")
				.eq("address_id", addressId!)
				.order("received_at", { ascending: false });
			if (error) throw error;
			return data;
		},
		enabled: !!addressId,
		refetchInterval: active ? 10000 : false,
	});
}

function useIngestItems(userId: string | undefined, active: boolean) {
	return useQuery({
		queryKey: ["ingest-items", userId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("ingest_items")
				.select(
					"id, source, source_type, category, title, data, email_id, created_at",
				);
			if (error) throw error;
			// Sort by price descending, then neighborhood alphabetically
			return data.sort((a, b) => {
				const aData = a.data as Record<string, unknown>;
				const bData = b.data as Record<string, unknown>;
				const aPrice =
					typeof aData.price === "number" ? aData.price : 0;
				const bPrice =
					typeof bData.price === "number" ? bData.price : 0;
				if (bPrice !== aPrice) return bPrice - aPrice;
				const aNeighborhood =
					typeof aData.neighborhood === "string"
						? aData.neighborhood
						: "";
				const bNeighborhood =
					typeof bData.neighborhood === "string"
						? bData.neighborhood
						: "";
				return aNeighborhood.localeCompare(bNeighborhood);
			});
		},
		enabled: !!userId,
		refetchInterval: active ? 10000 : false,
	});
}

function CopyButton({ text }: { text: string }): ReactElement {
	const [copied, setCopied] = useState(false);

	function handleCopy() {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	return (
		<button
			type="button"
			onClick={handleCopy}
			data-testid="copy-address-btn"
			className="text-fg-muted hover:text-accent ml-2 transition-colors"
			title="Copy email address"
		>
			{copied ? <FiCheck className="text-green-600" /> : <FiCopy />}
		</button>
	);
}

function InboxPage(): ReactElement {
	const { tab: activeTab } = Route.useSearch();
	const { data: session } = useSession();
	const { address, isLoading: addressLoading } = useIngestAddress();
	const { data: emails, isLoading: emailsLoading } = useIngestEmails(
		address?.id,
		activeTab === "emails",
	);
	const { data: items, isLoading: itemsLoading } = useIngestItems(
		session?.user?.id,
		activeTab === "items",
	);

	if (addressLoading) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-8">
				<div className="text-fg-muted text-sm">Loading...</div>
			</div>
		);
	}

	const fullEmail = address ? `${address.address_id}@baselineingest.dev` : "";

	return (
		<div className="mx-auto max-w-[960px] px-6 py-8">
			{address && (
				<div className="border-line mb-8 rounded-lg border p-4">
					<div className="text-fg-muted mb-1 text-xs font-medium tracking-wider uppercase">
						Your ingest address
					</div>
					<div className="flex items-center">
						<code
							className="text-accent text-lg font-medium"
							data-testid="ingest-address"
						>
							{fullEmail}
						</code>
						<CopyButton text={fullEmail} />
					</div>
					<p className="text-fg-faint mt-2 text-xs">
						Forward emails here to see them in your inbox below.
					</p>
				</div>
			)}

			<div className="border-line mb-4 flex gap-4 border-b">
				<Link
					to="/"
					search={{ tab: "emails" }}
					data-testid="tab-emails"
					className={`pb-2 text-sm font-medium transition-colors ${
						activeTab === "emails"
							? "text-accent border-accent border-b-2"
							: "text-fg-muted hover:text-fg-secondary"
					}`}
				>
					Emails
				</Link>
				<Link
					to="/"
					search={{ tab: "items" }}
					data-testid="tab-items"
					className={`pb-2 text-sm font-medium transition-colors ${
						activeTab === "items"
							? "text-accent border-accent border-b-2"
							: "text-fg-muted hover:text-fg-secondary"
					}`}
				>
					Extracted Items
					{items && items.length > 0 && (
						<span className="text-fg-faint ml-1.5 text-xs">
							{items.length}
						</span>
					)}
				</Link>
			</div>

			{activeTab === "emails" && (
				<EmailsList emails={emails} isLoading={emailsLoading} />
			)}
			{activeTab === "items" && (
				<ItemsList
					items={items as IngestItemRow[] | undefined}
					isLoading={itemsLoading}
				/>
			)}
		</div>
	);
}

function EmailsList({
	emails,
	isLoading,
}: {
	emails:
		| Array<{
				id: string;
				from_name: string | null;
				from_address: string;
				subject: string;
				received_at: string;
		  }>
		| undefined;
	isLoading: boolean;
}): ReactElement {
	if (isLoading) {
		return <div className="text-fg-muted text-sm">Loading emails...</div>;
	}

	if (!emails || emails.length === 0) {
		return (
			<div className="text-fg-faint border-line flex flex-col items-center gap-3 rounded-lg border border-dashed py-16">
				<FiMail className="text-3xl" />
				<p className="text-sm">
					No emails yet. Forward something to get started.
				</p>
			</div>
		);
	}

	return (
		<div className="border-line divide-line divide-y rounded-lg border">
			{emails.map((email) => (
				<Link
					key={email.id}
					to="/emails/$emailId"
					params={{ emailId: email.id }}
					data-testid="email-row"
					className="hover:bg-surface-overlay flex items-center gap-4 px-4 py-3 transition-colors"
				>
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-2">
							<span className="text-fg-secondary truncate text-sm font-medium">
								{email.from_name || email.from_address}
							</span>
							{email.from_name && (
								<span className="text-fg-faint truncate text-xs">
									{email.from_address}
								</span>
							)}
						</div>
						<div className="text-fg truncate text-sm">
							{email.subject || "(no subject)"}
						</div>
					</div>
					<span className="text-fg-faint text-xs whitespace-nowrap">
						{formatRelativeTime(email.received_at)}
					</span>
				</Link>
			))}
		</div>
	);
}

type IngestItemRow = {
	id: string;
	source: string;
	source_type: string;
	category: string;
	title: string | null;
	data: Record<string, unknown>;
	email_id: string | null;
	created_at: string;
};

function ItemsList({
	items,
	isLoading,
}: {
	items: IngestItemRow[] | undefined;
	isLoading: boolean;
}): ReactElement {
	if (isLoading) {
		return <div className="text-fg-muted text-sm">Loading items...</div>;
	}

	if (!items || items.length === 0) {
		return (
			<div className="text-fg-faint border-line flex flex-col items-center gap-3 rounded-lg border border-dashed py-16">
				<FiPackage className="text-3xl" />
				<p className="text-sm">
					No extracted items yet. Forward a StreetEasy alert email to
					extract listings.
				</p>
			</div>
		);
	}

	return (
		<div className="grid gap-3">
			{items.map((item) => (
				<Link
					key={item.id}
					to="/items/$itemId"
					params={{ itemId: item.id }}
					data-testid="item-card"
					className="border-line hover:bg-surface-overlay rounded-lg border p-4 transition-colors"
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2">
								<span
									className="text-fg-secondary text-sm font-medium"
									data-testid="item-title"
								>
									{item.title || "Untitled"}
								</span>
								<span className="bg-surface-overlay text-fg-muted rounded px-1.5 py-0.5 text-[10px] font-medium uppercase">
									{item.source}
								</span>
							</div>
							<ItemDataSummary data={item.data} />
						</div>
						<span className="text-fg-faint text-xs whitespace-nowrap">
							{formatRelativeTime(item.created_at)}
						</span>
					</div>
				</Link>
			))}
		</div>
	);
}

function ItemDataSummary({
	data,
}: {
	data: Record<string, unknown>;
}): ReactElement | null {
	const parts: string[] = [];

	if (typeof data.price === "number") {
		parts.push(formatPrice(data.price));
	}
	if (typeof data.bedrooms === "number") {
		parts.push(`${data.bedrooms} bed`);
	}
	if (typeof data.bathrooms === "number") {
		parts.push(`${data.bathrooms} bath`);
	}
	if (typeof data.sqft === "number") {
		parts.push(`${data.sqft} sqft`);
	}
	if (typeof data.neighborhood === "string") {
		parts.push(data.neighborhood);
	}
	if (typeof data.available_date === "string") {
		const date = data.available_date.replace(/^Available\s*/i, "").trim();
		parts.push(date === "now" ? "Available now" : `Avail ${date}`);
	}

	if (parts.length === 0) return null;

	return (
		<div className="text-fg-muted mt-1 text-xs">{parts.join(" · ")}</div>
	);
}
